import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardProfileContextService, DashboardUserContextService } from '@rma/authentication/public/user';
import { CurrentSubscriptionService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { GenericFeatureFlagService } from '@rma/common-ui-ng/feature-flags/internal';
import { DeviceIdentificationService } from '@rma/common-ui-ng/util';
import { PlatformService } from '@rma/generic/util/environment';
import { nonNullable } from '@rma/generic/util/operators/non-nullable';
import { LDContextCommon, LDMultiKindContext } from 'launchdarkly-js-sdk-common';
import { combineLatest, filter, map, startWith } from 'rxjs';

import { FeatureUser, mapDataToFeatureUser } from '../util-feature-user-mapper/feature-user.mapper';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagUserIdentificationService {
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly userContextService = inject(DashboardUserContextService);
  private readonly currentSubscriptionService = inject(CurrentSubscriptionService);
  private readonly deviceIdService = inject(DeviceIdentificationService, { optional: true });
  private readonly featureFlagService = inject(GenericFeatureFlagService<string>);
  private readonly platformService = inject(PlatformService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  public initialise() {
    if (this.platformService.isPlatformServer) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      combineLatest({
        profileContext: this.profileContextService.profileContext$.pipe(nonNullable()),
        userContext: this.userContextService.userContext$.pipe(nonNullable()),
        subscription: this.currentSubscriptionService.currentSubscription$.pipe(startWith(null)),
      })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(({ profileContext, userContext, subscription }) => mapDataToFeatureUser(profileContext, userContext, subscription)),
          filter((user) => Boolean(user)),
        )
        .subscribe((user) => this.identifyUser(user));
    });
  }

  private identifyUser(currentUser: FeatureUser | null) {
    const deviceId = this.deviceIdService?.getDeviceId();

    const userContext = this.getLdUserContext(currentUser);
    const deviceContext = this.getLdDeviceContext(deviceId, Boolean(userContext?.anonymous));

    const context: LDMultiKindContext = {
      kind: 'multi',
    };

    if (deviceContext) {
      context['device'] = deviceContext;
    }

    if (userContext) {
      context['user'] = userContext;
    }

    this.featureFlagService.identify(context);
  }

  private getLdDeviceContext(deviceId: string | undefined, isAnonymous: boolean): LDContextCommon | null {
    //If this works, we should extend the custom with browser information using bowser or ua parser
    return deviceId
      ? {
          key: deviceId,
          anonymous: isAnonymous,
          custom: {},
        }
      : null;
  }

  private getLdUserContext(currentUser: FeatureUser | null): LDContextCommon | null {
    return currentUser ? currentUser : { anonymous: true };
  }
}
